/* eslint-disable import/no-cycle*/
import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { replaceNewLines, joinArrayOfStringsWithNewLines } from 'utils';
import { Button, RadioButton, Input, Checkbox } from 'sharedComponents';
import {
  PACKAGING_TYPES,
  GIFT_NOTE_TYPES,
  NOTE_OPTIONS,
  GIFT_ERRORS,
  NO_GIFT_PACKAGING,
  FREE_BG_GIFT_PACKAGING,
  GIFT_PACKAGING_REGULAR,
  GIFT_PACKAGING_ELEVATED,
  GIFT_RECEIPT_TITLE,
} from 'constants/index';
import { RadioGroup, Divider, Typography } from '@material-ui/core';

import gift from 'assets/images/gift.png';

const initialGiftOptions = {
  giftWrapCode: PACKAGING_TYPES.NO_PACKAGING,
  giftCard: GIFT_NOTE_TYPES.NO_GIFT_NOTE,
  giftReceipt: false,
};

export const getMessage = (options) => {
  if (options.giftMessage1 === 'PLAIN') {
    return '';
  }

  const { giftMessage1, giftMessage2, giftMessage3, giftMessage4, giftMessage5 } = options;

  return joinArrayOfStringsWithNewLines([giftMessage1, giftMessage2, giftMessage3, giftMessage4, giftMessage5]);
};

export const parseTextarea = (msg) => {
  const rows = msg.split('\n');
  const [giftMessage1, giftMessage2, giftMessage3, giftMessage4, giftMessage5] = rows;

  return {
    giftMessage1: giftMessage1 || '',
    giftMessage2: giftMessage2 || '',
    giftMessage3: giftMessage3 || '',
    giftMessage4: giftMessage4 || '',
    giftMessage5: giftMessage5 || '',
  };
};

const GiftOptions = ({
  classes,
  clientId,
  itemskuId,
  itemGiftOptions,
  addGiftOptions,
  addInStoreGiftOptions,
  onClose,
  isBGAssociate,
  isGiftReceiptEnabled,
  isConciergeBag,
}) => {
  const [customMessage, setCustomMessage] = useState(getMessage(itemGiftOptions || {}));
  const [giftOptions, setGiftOptions] = useState(itemGiftOptions || (isConciergeBag ? {} : initialGiftOptions));
  const [giftReceipt, setGiftReceipt] = React.useState(giftOptions.giftReceipt);
  const [errorMessageChars, setErrorMessageChars] = useState(false);
  const [errorMessageRows, setErrorMessageRows] = useState(false);

  const isElevatedGiftPackaging = isBGAssociate && giftOptions.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING;
  const isGiftChecked = giftOptions.giftWrapCode === PACKAGING_TYPES.GIFT_PACKAGING || isElevatedGiftPackaging;
  const isFreeMsgChecked = giftOptions.giftCard === GIFT_NOTE_TYPES.FREE_CUSTOM_MESSAGE;

  const emptyCustomMessage = !customMessage || !customMessage.trim();

  const onSaveClick = () => {
    if (isConciergeBag) {
      addGiftOptions({ clientId, itemskuId, giftOptions: { ...giftOptions, giftReceipt } });
    } else {
      addInStoreGiftOptions(itemskuId, { ...giftOptions, giftReceipt });
    }

    onClose();
  };

  const renderPackagingExtra = () => {
    if (isGiftChecked) {
      return (
        <>
          <img src={gift} alt="gift package" />
          <Typography variant="body2" color="textSecondary">
            If you ship gift to an address different from your billing address, a gift receipt will be included in your
            order.
          </Typography>
        </>
      );
    }

    return null;
  };

  // storage for bad textarea rows
  const badRows = new Set();

  const validateTextarea = (val, idx) => {
    if (!badRows.size) {
      setErrorMessageChars(false);
    }

    if (val.length > 26) {
      setErrorMessageChars(true);
      badRows.add(idx);
    } else {
      badRows.delete(idx);
    }
  };

  const textHandle = (msg) => {
    // prevent first line to be blank
    if (!msg.trim().length) {
      setCustomMessage('');
      return;
    }

    const rows = msg.split('\n');
    const newMsg = replaceNewLines(msg);

    setErrorMessageRows(rows.length > 5);

    rows.forEach((row, idx) => {
      validateTextarea(row, idx);
    });

    setGiftOptions({
      ...giftOptions,
      ...parseTextarea(newMsg),
    });

    setCustomMessage(newMsg);
  };

  const radioHandle = (attr) => (e) => {
    const { value } = e.target;
    let messages = {};
    if (value !== GIFT_NOTE_TYPES.FREE_CUSTOM_MESSAGE) {
      setCustomMessage('');
      setErrorMessageChars(false);
      setErrorMessageRows(false);
    }

    if (value === PACKAGING_TYPES.NO_PACKAGING || GIFT_NOTE_TYPES.NO_GIFT_NOTE) {
      // reset messages
      messages = parseTextarea('');
    }

    if (value === GIFT_NOTE_TYPES.FREE_BLANK_NOTE) {
      messages = parseTextarea('PLAIN');
    }

    setGiftOptions({
      ...giftOptions,
      ...messages,
      giftCard: GIFT_NOTE_TYPES.NO_GIFT_NOTE, // giftCard will be set to NO_GIFT_NOTE, whenever giftWrapCode changes.
      [attr]: value,
    });
  };

  const BG_PACKAGING = isConciergeBag ? GIFT_PACKAGING_ELEVATED : FREE_BG_GIFT_PACKAGING;
  const PACKAGING_OPTIONS = React.useMemo(
    () => [NO_GIFT_PACKAGING, isBGAssociate ? BG_PACKAGING : GIFT_PACKAGING_REGULAR],
    [isBGAssociate],
  );

  return (
    <div className={classes.container}>
      <div className={classes.body}>
        <RadioGroup
          name="gift-packaging-radio-btns"
          value={giftOptions.giftWrapCode}
          onChange={radioHandle('giftWrapCode')}
          data-testid="gift-packaging-radio-btns"
        >
          {PACKAGING_OPTIONS.map(({ value, label, term }) => (
            <RadioButton className={classes.giftOption} key={value} value={value}>
              {label}
              {term && <span className={classes.giftOptionTerm}>{term}</span>}
            </RadioButton>
          ))}
        </RadioGroup>
        {renderPackagingExtra()}
        {isGiftChecked && (
          <div className={classes.radioGroup}>
            <Divider className={classes.divider} />
            <RadioGroup
              name="gift-card-radio-btns"
              value={giftOptions.giftCard}
              onChange={radioHandle('giftCard')}
              data-testid="gift-card-radio-btns"
            >
              {NOTE_OPTIONS.map(({ value, label }) => (
                <RadioButton key={value} value={value} label={label} />
              ))}
            </RadioGroup>
            {isFreeMsgChecked && (
              <div className={classes.textarea}>
                <Input
                  placeholder="Enter message here..."
                  value={customMessage}
                  onChange={textHandle}
                  multiline
                  minRows={6}
                  variant="outlined"
                />
                <Typography variant="body2">26 characters per line, 5 lines</Typography>
              </div>
            )}
            {errorMessageChars && <Typography color="error">{GIFT_ERRORS.CHARS}</Typography>}
            {errorMessageRows && <Typography color="error">{GIFT_ERRORS.ROWS}</Typography>}
          </div>
        )}
        {isGiftReceiptEnabled && (
          <>
            <Divider className={classes.divider} />
            <Checkbox
              label={GIFT_RECEIPT_TITLE}
              classes={{ labelRoot: classes.checkbox }}
              onChange={() => setGiftReceipt(!giftReceipt)}
              checked={giftReceipt}
              data-testid="gift-receipt-checkbox"
            />
          </>
        )}
      </div>
      <div className={classes.footer}>
        <Button
          disabled={errorMessageChars || errorMessageRows || (isFreeMsgChecked && emptyCustomMessage)}
          onClick={onSaveClick}
          data-testid="save-gift-options-btn"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

GiftOptions.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  clientId: PropTypes.string,
  itemskuId: PropTypes.string,
  onClose: PropTypes.func,
  addInStoreGiftOptions: PropTypes.func,
  addGiftOptions: PropTypes.func,
  isConciergeBag: PropTypes.bool,
  isBGAssociate: PropTypes.bool,
  isGiftReceiptEnabled: PropTypes.bool,
};

GiftOptions.defaultProps = {
  classes: {},
  isBGAssociate: false,
  isGiftReceiptEnabled: false,
  isConciergeBag: false,
  addInStoreGiftOptions: () => null,
};

export default GiftOptions;
